/**
 * Tools, and what they are needed for.
 *
 * There is no mining *time* in this game yet - a block comes apart the moment
 * you hit it - so "the right tool" cannot mean "faster". It means "at all":
 * a tool is required for the materials it exists to work, and refused for
 * everything it does not.
 *
 * That is a stronger rule than Minecraft's, which lets you punch stone and
 * simply gives you nothing for it. Refusing outright, with a reason, is
 * clearer: a block that vanishes and yields nothing looks like a bug, and a
 * player who does not already know the convention has no way to learn it.
 *
 * A сокира is the one tool here that is never *required*: wood always came
 * away in the hand and still does. What it changes is how long that takes -
 * see breaking.ts - which is the other half of "the right tool", and the
 * half that only exists now that blows are counted.
 *
 * The rule has a trap in it, and it is the same one the brick stove had: if
 * stone needs a pick, and a pick needs stone, the game is unwinnable from the
 * first block. The wooden tier exists to break that circle, and a test walks
 * the whole progression from an empty bag to make sure it stays broken.
 */

import { blockMaterial, type Material } from "./material.js";
import { BlockId } from "./registry.js";

export type ToolKind = "pickaxe" | "shovel" | "sabre" | "hoe" | "axe";

/**
 * The tool a material must be worked with, or null if hands will do.
 *
 * Only two materials demand one. Earth is dug by hand everywhere except clay -
 * see needsTool - and wood, plants and cloth come away in the hand, which is
 * how the game starts at all: the first thing anyone does is pull up wild
 * wheat and break a branch off an oak.
 */
const REQUIRED: Readonly<Partial<Record<Material, ToolKind>>> = {
  stone: "pickaxe",
  glass: "pickaxe",
};

/**
 * What must be held to get anything out of this block.
 *
 * Clay is the exception inside "earth": it is the one soil that is a resource
 * rather than terrain, it lies under a lake, and scooping it is exactly what a
 * spade is for. Without that the spade would be an item with no purpose until
 * mining speed exists, and a tool that does nothing is worse than no tool.
 */
/**
 * Soils that are a resource rather than terrain, and so want a spade.
 *
 * Clay under a lake, peat out of a bog, and the titanium sands - all of them
 * things you go and dig, as against the dirt you shift to get at them.
 */
const SPADE_SOILS: readonly number[] = [
  BlockId.Clay, BlockId.Peat, BlockId.Ilmenite, BlockId.Rutile,
];

export function requiredTool(blockId: number): ToolKind | null {
  if (SPADE_SOILS.includes(blockId)) return "shovel";
  return REQUIRED[blockMaterial(blockId)] ?? null;
}
